import { useMutation } from '@apollo/client';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Stepper from '@material-ui/core/Stepper';
import gql from 'graphql-tag';
import React, { useEffect, useMemo, useState } from 'react';
import DocumentCapture from './DocumentCapture';
import SelfieCapture from './SelfieCapture';

const SIGNED_URL_TO_UPLOAD = gql`
  mutation SignedUrlToUpload($file: String!) {
    signedUrlToUpload(file: $file) {
      uploadUrl
      finalPath
    }
  }
`;

const IDENTITY_VERIFICATION_MANUAL_SUBMIT = gql`
  mutation IdentityVerificationManualSubmit(
    $input: IdentityVerificationManualSubmitInput!
  ) {
    identityVerificationManualSubmit(input: $input) {
      status
    }
  }
`;

interface Props {
  open: boolean;
  onClose: () => void;
  compRegistrationId: string;
  userName?: string;
  country?: string;
  onSuccess: () => void;
}

const STEPS = ['document', 'selfie', 'review'];

const ManualIdentityVerificationForm = ({
  open,
  onClose,
  compRegistrationId,
  userName = '',
  country = '',
  onSuccess,
}: Props) => {
  const [activeStep, setActiveStep] = useState(0);
  const [documentBlob, setDocumentBlob] = useState<Blob | null>(null);
  const [selfieBlob, setSelfieBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [signedUrlToUpload] = useMutation(SIGNED_URL_TO_UPLOAD);
  const [manualSubmit] = useMutation(IDENTITY_VERIFICATION_MANUAL_SUBMIT);

  const resetForm = () => {
    setActiveStep(0);
    setDocumentBlob(null);
    setSelfieBlob(null);
    setError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (!submitting) {
      resetForm();
      onClose();
    }
  };

  const uploadFile = async (
    filePath: string,
    blob: Blob,
    contentType: string
  ): Promise<string> => {
    const { data } = await signedUrlToUpload({
      variables: { file: filePath },
    });
    const uploadUrl = data?.signedUrlToUpload?.uploadUrl;
    if (!uploadUrl) throw new Error('Failed to get upload URL');

    await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: blob,
    });

    return data?.signedUrlToUpload?.finalPath;
  };

  const handleSubmit = async () => {
    if (!documentBlob || !selfieBlob) {
      setError('Please provide both a document and a selfie.');
      return;
    }
    setSubmitting(true);
    setError(null);

    try {
      const docPath = `identity_verification/${compRegistrationId}_document.jpg`;
      const selfiePath = `identity_verification/${compRegistrationId}_selfie.jpg`;

      const documentStoredPath = await uploadFile(
        docPath,
        documentBlob,
        'image/jpeg'
      );
      const selfieStoredPath = await uploadFile(
        selfiePath,
        selfieBlob,
        'image/jpeg'
      );

      await manualSubmit({
        variables: {
          input: {
            compRegistrationId,
            documentUrl: documentStoredPath,
            selfieUrl: selfieStoredPath,
          },
        },
      });

      resetForm();
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    setError(null);
    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const canProceedFromDocument = !!documentBlob;
  const canProceedFromSelfie = !!selfieBlob;

  const documentPreviewUrl = useMemo(
    () => (documentBlob ? URL.createObjectURL(documentBlob) : null),
    [documentBlob]
  );
  const selfiePreviewUrl = useMemo(
    () => (selfieBlob ? URL.createObjectURL(selfieBlob) : null),
    [selfieBlob]
  );

  useEffect(() => {
    return () => {
      documentPreviewUrl && URL.revokeObjectURL(documentPreviewUrl);
    };
  }, [documentPreviewUrl]);

  useEffect(() => {
    return () => {
      selfiePreviewUrl && URL.revokeObjectURL(selfiePreviewUrl);
    };
  }, [selfiePreviewUrl]);

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box width="100%" mb={2}>
            <Box mb={1} fontWeight="fontWeightMedium">
              Nationality Document
            </Box>
            <Box mb={1} fontSize="body2.fontSize" color="text.secondary">
              Take a photo of your government-issued document that proves your
              nationality. It must contain your name and country.
            </Box>
            <Box mb={2} p={1.5} bgcolor="action.hover" borderRadius={1}>
              <Box fontSize="body2.fontSize" fontWeight="fontWeightMedium">
                Name: <strong>{userName}</strong>
              </Box>
              <Box fontSize="body2.fontSize" fontWeight="fontWeightMedium">
                Country: <strong>{country}</strong>
              </Box>
            </Box>
            <DocumentCapture
              onCapture={setDocumentBlob}
              onClear={() => setDocumentBlob(null)}
              disabled={submitting}
            />
          </Box>
        );
      case 1:
        return (
          <Box width="100%" mb={2}>
            <Box mb={1} fontWeight="fontWeightMedium">
              Selfie
            </Box>
            <Box mb={2} fontSize="body2.fontSize" color="text.secondary">
              Take a selfie to verify your identity. Position your face within
              the oval.
            </Box>
            <SelfieCapture onCapture={setSelfieBlob} disabled={submitting} />
          </Box>
        );
      case 2:
        return (
          <Box width="100%" mb={2}>
            <Box mb={2} fontWeight="fontWeightMedium">
              Review and submit
            </Box>
            <Box
              display="flex"
              flexDirection="row"
              flexWrap="wrap"
              justifyContent="center"
              mb={2}
              style={{ gap: 16 }}
            >
              {documentBlob && documentPreviewUrl && (
                <Box>
                  <Box
                    mb={0.5}
                    fontSize="body2.fontSize"
                    fontWeight="fontWeightMedium"
                  >
                    Document
                  </Box>
                  <img
                    src={documentPreviewUrl}
                    alt="Document preview"
                    style={{
                      maxWidth: 160,
                      maxHeight: 200,
                      objectFit: 'contain',
                      borderRadius: 4,
                      border: '1px solid rgba(0,0,0,0.12)',
                    }}
                  />
                </Box>
              )}
              {selfieBlob && selfiePreviewUrl && (
                <Box>
                  <Box
                    mb={0.5}
                    fontSize="body2.fontSize"
                    fontWeight="fontWeightMedium"
                  >
                    Selfie
                  </Box>
                  <img
                    src={selfiePreviewUrl}
                    alt="Selfie preview"
                    style={{
                      maxWidth: 160,
                      maxHeight: 200,
                      objectFit: 'contain',
                      borderRadius: 4,
                      border: '1px solid rgba(0,0,0,0.12)',
                    }}
                  />
                </Box>
              )}
            </Box>
            <Box fontSize="body2.fontSize" color="text.secondary">
              By submitting, you confirm that the documents are authentic and
              belong to you.
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableBackdropClick={submitting}
      disableEscapeKeyDown={submitting}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <span>Manual identity verification</span>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={submitting}
            size="small"
          >
            ×
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          style={{ marginBottom: 24 }}
        >
          <Step>
            <StepLabel>Document</StepLabel>
          </Step>
          <Step>
            <StepLabel>Selfie</StepLabel>
          </Step>
          <Step>
            <StepLabel>Review</StepLabel>
          </Step>
        </Stepper>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          width="100%"
          minHeight={280}
        >
          {renderStepContent()}
        </Box>
        {error && (
          <Box mt={2} color="error.main" textAlign="center">
            {error}
          </Box>
        )}
      </DialogContent>
      <DialogActions style={{ padding: '16px 24px' }}>
        <Box display="flex" justifyContent="space-between" width="100%">
          <Box>
            {activeStep > 0 && (
              <Button onClick={handleBack} disabled={submitting}>
                Back
              </Button>
            )}
          </Box>
          <Box>
            {activeStep < STEPS.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={
                  submitting ||
                  (activeStep === 0 && !canProceedFromDocument) ||
                  (activeStep === 1 && !canProceedFromSelfie)
                }
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                disabled={submitting}
                onClick={handleSubmit}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </Button>
            )}
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ManualIdentityVerificationForm;
